import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageLoadTracking } from '@/hooks/pageLoadTracking/usePageLoadTracking'

import { debounce } from '@/utils/functionalUtils'

import { ButtonSpinner, PrimaryButton } from '@/components/buttons/primaryButton'
import { Option } from '@/components/option'
import { PageTitle } from '@/components/pageTitle'
import { FooterPolicy } from '@/components/policyLinkBlock'
import { TextHTML } from '@/components/typography'

import { useAuth } from '@/auth/useAuth'
import { useSession } from '@/core/session/useSession'
import { trackPageComplete } from '@/events/events'

import { TMultiSelectPageProps } from './types'

export function MultiSelectPage({
  pageName,
  resetElementIndex = 0,
  pageConfig,
  setItem,
  setLastVisitedItem,
  config,
}: TMultiSelectPageProps) {
  const { user } = useAuth()
  const sessionContext = useSession()
  const [selectedOptions, setSelectedOptions] = useState<Array<number>>([])
  const [isLoading, setIsloading] = useState(false)

  const {
    title,
    tabTitle,
    options = [],
    nextUrl,
    pageDescription,
    backgroundImage,
    decorativeImageOptions,
    optionsContainerBackgroundImage,
    buttonName,
    customCheckbox,
    showDefaultCircle,
    policyBlock = false,
    hideIndicator,
  } = useMemo(() => pageConfig, [pageConfig])
  const navigate = useNavigate()

  const pageTitle = typeof title === 'function' ? title() : title

  usePageLoadTracking({ pageName, config, user, title: tabTitle || pageTitle })

  const getSelectedTitles = useCallback((): string[] => {
    const selectedTitles: string[] = []
    for (const index of selectedOptions) {
      const optionTitle = options[index]?.title
      if (optionTitle) {
        selectedTitles.push(optionTitle)
      }
    }
    return selectedTitles
  }, [selectedOptions, options])

  const getSelectedValues = useCallback(
    (indexes: number[]): string => {
      const selectedValues: (number | string)[] = []
      for (const index of indexes) {
        const optionValue = options[index]?.storageValue
        if (optionValue) {
          selectedValues.push(optionValue)
        } else {
          selectedValues.push(index)
        }
      }
      return selectedValues.join(', ')
    },
    [options]
  )

  const nextPage = async () => {
    setItem(pageName, getSelectedValues(selectedOptions))
    setIsloading(true)

    const selectedTitles = getSelectedTitles()
    trackPageComplete(
      pageName,
      selectedTitles,
      config.PARTNER_ID,
      config.APP_ID,
      config.PAGE_SET_ID,
      sessionContext,
      user
    )
    await debounce(() => {
      navigate(nextUrl)
      setLastVisitedItem(pageName)
    }, 300)()
  }

  const selectOption = useCallback(
    (currentOption: number) => {
      if (currentOption === resetElementIndex) {
        setSelectedOptions([resetElementIndex])
        return
      }
      if (!selectedOptions.includes(currentOption)) {
        return setSelectedOptions((prevState) => {
          if (prevState.includes(resetElementIndex)) {
            return [currentOption]
          }

          return [...prevState, currentOption]
        })
      } else {
        return setSelectedOptions((prevState) => {
          if (prevState.includes(resetElementIndex)) {
            return []
          }
          return [
            ...prevState.filter(
              (option) => option !== currentOption || currentOption === resetElementIndex
            ),
          ]
        })
      }
    },
    [resetElementIndex, selectedOptions]
  )

  const className = `page page-survey page-${pageName}`
  const hasBackgroundImage = Boolean(backgroundImage)

  let optionContainerClassName = 'survey-options-container'
  if (hasBackgroundImage) {
    optionContainerClassName += ' with-image'
  }
  return (
    <div className={className}>
      <PageTitle title={pageTitle} />
      {pageDescription && <TextHTML className="page-description" title={pageDescription} />}
      <div className={optionContainerClassName}>
        {options?.map((option, index) => (
          <Option
            key={`${option.title}${index}`}
            index={index}
            title={option.title}
            description={option.description}
            image={option.image}
            decorativeImage={decorativeImageOptions}
            emoji={option.emoji}
            selectOption={selectOption}
            selectedOptions={selectedOptions}
            customCheckbox={customCheckbox}
            showDefaultCircle={showDefaultCircle}
            hideIndicator={hideIndicator}
          />
        ))}
        {optionsContainerBackgroundImage
          ? optionsContainerBackgroundImage(selectedOptions)
          : undefined}
      </div>
      <div className="button-container float">
        <PrimaryButton disabled={isLoading || !selectedOptions.length} onClick={nextPage}>
          {isLoading ? <ButtonSpinner /> : buttonName || 'Next'}
        </PrimaryButton>
      </div>
      {policyBlock && <FooterPolicy />}
    </div>
  )
}
